import { useContext, useEffect, useState } from "react";
import "./Cards.css";
import { toast } from "react-toastify";
import Card from "./Card";
import { AuthContext } from "../../../contexts/AuthContext";
import { getAdminDashboardSummary } from "../../../hooks/admin_api";

// Import Modal Components
import AddContributionModal from "../AdminModals/AddContributionModal";
import AddBenefitModal from "../AdminModals/AddBenefitModal";
import BulkEmailModal from "../AdminModals/BulkEmailModal";

// Icons
import {
  FaUsers,
  FaPlus,
  FaLock,
  FaHandHoldingHeart,
  FaComments,
  FaCheckCircle,
  FaClock,
  FaTimesCircle,
} from "react-icons/fa";
import { UilUsdSquare, UilClipboardAlt } from "@iconscout/react-unicons";
import { fetchSalesSummary } from "../../../hooks/api";
import BulkSmsModal from "../../modals/SendBulkSMSModal";
import SummaryStats from "./SummaryStats";
import UserContributions from "../../../pages/user/UserContributions";

const Cards = () => {
  const { user, token } = useContext(AuthContext);
  const [statData, setStats] = useState([]);
  const [summaryData, setSummaryData] = useState(null);

  // Remove this loading state or keep it for other purposes
  // const [loading, setLoading] = useState(true);

  // Modal states
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [showContributionModal, setShowContributionModal] = useState(false);
  const [showBenefitModal, setShowBenefitModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showAccessDeniedModal, setShowAccessDeniedModal] = useState(false);

  // Check if user is admin OR superadmin
  const isAdmin = user?.role === "admin";
  const isSuperAdmin = user?.role === "superAdmin" || user?.role === "superadmin";
  const isAdminOrSuperAdmin = isAdmin || isSuperAdmin;

  // Remove the old loading useEffect or keep it for other data fetching

  // Handle action button clicks
  const handleActionClick = (actionType) => {
    console.log(`Action clicked: ${actionType}, isAdmin: ${isAdmin}`);

    if (!isAdmin) {
      setShowAccessDeniedModal(true);
      return;
    }

    switch (actionType) {
      case "addContribution":
        console.log("Opening contribution modal");
        setShowContributionModal(true);
        break;
      case "addBenefit":
        console.log("Opening benefit modal");
        setShowBenefitModal(true);
        break;
      case "sendSMS":
        console.log("Opening SMS modal");
        setShowSmsModal(true);
        break;
      case "sendEmail":
        console.log("Opening email modal");
        setShowEmailModal(true);
        break;
      default:
        console.log("Unknown action type:", actionType);
        break;
    }
  };

  // REMOVE THIS ENTIRE BLOCK - This is what's causing the forever loading
  // if (loading) {
  //   return (
  //     <div className="text-center my-5 py-5">
  //       <div
  //         className="spinner-border text-primary"
  //         style={{ width: "3rem", height: "3rem" }}
  //         role="status"
  //       ></div>
  //       <p className="mt-3">Loading dashboard...</p>
  //     </div>
  //   );
  // }

  // If not admin OR superadmin, show access denied
  if (!isAdminOrSuperAdmin) {
    return (
      <div className="container-fluid py-5">
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6">
            <div className="card text-center border-danger shadow">
              <div className="card-body py-5 px-3">
                <FaLock size={80} className="text-danger mb-4" />
                <h3 className="card-title mb-3">Admin Access Required</h3>
                <p className="card-text mb-4">
                  You need administrator privileges to access this dashboard.
                  Please contact your system administrator.
                </p>
                <a href="/" className="btn btn-primary px-4">
                  Go to Homepage
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Get correct badge color and text based on role
  const getAdminBadge = () => {
    if (isSuperAdmin) {
      return {
        text: "Super Admin",
        color: "bg-warning text-dark",
        icon: <FaLock className="me-1" />,
      };
    }
    if (isAdmin) {
      return {
        text: "Administrator",
        color: "bg-danger",
        icon: <FaLock className="me-1" />,
      };
    }
    return null;
  };

  const adminBadge = getAdminBadge();

  // Action cards data
  const actionCards = [
    {
      id: 1,
      title: "Add Contributions",
      description: "Add and approve contributions for members",
      icon: <FaPlus size={40} />,
      color: "linear-gradient(135deg, #a20a95ff 0%, #c9097fff 100%)",
      actionType: "addContribution",
    },
    {
      id: 2,
      title: "Add Benefits",
      description: "Manage member benefit requests",
      icon: <FaHandHoldingHeart size={40} />,
      color: "linear-gradient(135deg, #07a714ff 0%, #aec011ff 100%)",
      actionType: "addBenefit",
    },
    {
      id: 3,
      title: "Send SMS To All Users",
      description: "Broadcast SMS messages",
      icon: <FaComments size={40} />,
      color: "linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)",
      actionType: "sendSMS",
    },
  ];

  return (
    <div className="container-fluid px-3 px-md-4 px-lg-5">

      {/* Admin Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
        <div>
          <p className="text-muted mb-0">
            {isSuperAdmin ? "Super Admin Dashboard" : "Admin Dashboard"}
          </p>
          <span className={`badge p-2 mt-2 mt-md-0 text-white bg-success`}>
            v2.4.0
          </span>
        </div>
        {adminBadge && (
          <span className={`badge p-2 mt-2 mt-md-0 ${adminBadge.color}`}>
            {adminBadge.icon}
            {adminBadge.text}
          </span>
        )}
      </div>

      {/* Role Message */}
      {isSuperAdmin && (
        <div className="alert alert-warning mb-4">
          <i className="bi bi-info-circle me-2"></i>
          <strong>Super Admin Mode:</strong> You can view all dashboard data.
          Only Administrators can perform actions.
        </div>
      )}

      {/* Main Stat Cards - SummaryStats handles its own loading */}
      <div className="row g-3 mb-4">
        <SummaryStats />
      </div>

      {/* Action Cards Grid */}
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0">Quick Actions</h4>
            {isSuperAdmin && (
              <span className="badge bg-secondary">
                <i className="bi bi-eye me-1"></i> View Only
              </span>
            )}
          </div>
        </div>

        {actionCards.map((card) => (
          <div className="col-12 col-sm-6 col-lg-3 mb-3" key={card.id}>
            <div
              className={`card text-white shadow-sm h-100 action-card ${!isAdmin ? "opacity-75" : ""
                }`}
              style={{
                background: card.color,
                minHeight: "180px",
                cursor: isAdmin ? "pointer" : "not-allowed",
              }}
              onClick={() => handleActionClick(card.actionType)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleActionClick(card.actionType);
                }
              }}
            >
              <div className="card-body d-flex flex-column justify-content-center align-items-center p-4 position-relative">
                <div className="mb-3">{card.icon}</div>
                <h5 className="card-title text-center mb-2">{card.title}</h5>
                <p className="small text-center mb-0 opacity-90">
                  {card.description}
                </p>

                {!isAdmin && (
                  <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-50 rounded">
                    <span className="badge bg-secondary">
                      <i className="bi bi-lock me-1"></i> Admin Only
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modals Section - Keep all your modals as is */}
      {isAdmin && (
        <>
          <BulkSmsModal
            show={showSmsModal}
            onHide={() => setShowSmsModal(false)}
            token={token}
            adminId={user?.id}
            adminName={user?.full_name}
          />

          <AddContributionModal
            show={showContributionModal}
            onHide={() => setShowContributionModal(false)}
            token={token}
            adminId={user?.id}
            adminName={user?.full_name}
          />

          <AddBenefitModal
            show={showBenefitModal}
            onHide={() => setShowBenefitModal(false)}
            token={token}
            adminId={user?.id}
            adminName={user?.full_name}
          />

          <BulkEmailModal
            show={showEmailModal}
            onHide={() => setShowEmailModal(false)}
            token={token}
            adminId={user?.id}
            adminName={user?.full_name}
          />
        </>
      )}

      {/* Access Denied Modal for SuperAdmin */}
      {showAccessDeniedModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setShowAccessDeniedModal(false)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content rounded-4 shadow">
              <div className="modal-header border-0">
                <h5 className="modal-title text-danger">
                  <i className="bi bi-shield-lock me-2"></i>
                  Access Restricted
                </h5>
                <button
                  className="btn-close"
                  onClick={() => setShowAccessDeniedModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="text-center mb-4">
                  <div className="mb-3">
                    <i
                      className="bi bi-person-check"
                      style={{ fontSize: "4rem", color: "#dc3545" }}
                    ></i>
                  </div>
                  <h5 className="text-danger mb-3">
                    Administrator Access Required
                  </h5>
                  <p className="mb-0">
                    This action is restricted to <strong>Administrators</strong>{" "}
                    only.
                    <br />
                    <small className="text-muted">
                      Super Admins have view-only access to the dashboard.
                    </small>
                  </p>
                </div>
                <div className="alert alert-info">
                  <small>
                    <i className="bi bi-info-circle me-1"></i>
                    <strong>Super Admin Role:</strong> Can manage users and view
                    all data.
                    <br />
                    <strong>Admin Role:</strong> Can perform day-to-day
                    operations.
                  </small>
                </div>
              </div>
              <div className="modal-footer border-0">
                <button
                  className="btn btn-secondary w-100"
                  onClick={() => setShowAccessDeniedModal(false)}
                >
                  I Understand
                </button>
              </div>
            </div>
          </div>
        </div>
      )}



      {/* Bootstrap Icons */}
      <link
        rel="stylesheet"
        href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.0/font/bootstrap-icons.css"
      />
    </div>
  );
};


export default Cards;